// The single place that talks to localStorage / sessionStorage. Nothing here
// throws, so callers never need their own try/catch.
//
// Storage can fail in several distinct ways: Safari with "Block All Cookies"
// has no binding at all, so naming it throws ReferenceError; Chromium and
// sandboxed iframes keep the property but deny access with a SecurityError; and
// Safari private mode or an exhausted quota serve reads while refusing writes.
// An unguarded access during first render blanked the whole app once already
// (CREATOR-V2-5G).
//
// Refused writes are kept in an in-memory fallback, shared per key rather than
// held by each caller. Two components reading the same key have to agree, or a
// flag written on one screen is invisible on the next - which looped the
// welcome flow, because onboarding marked it shown and the index page, mounting
// fresh, never saw the write and redirected straight back.
//
// The fallback is consulted BEFORE storage, because it only ever holds keys
// whose write storage refused, which makes it the newer truth wherever it has
// an entry. A refused removal stores a null tombstone for the same reason, so
// the stale value still sitting in storage does not read as current.
//
// The fallback lasts one page load. Nothing can survive a reload without
// storage; the point is that the app stays consistent with itself in between.

type WebStorageAccess = {
	readonly read: (key: string) => string | null;
	readonly write: (key: string, value: string | undefined) => void;
};

function createWebStorageAccess(getStorage: () => Storage): WebStorageAccess {
	// null is a tombstone: "removed, but storage still holds the old value".
	const memoryFallback = new Map<string, string | null>();

	return {
		read(key) {
			if (memoryFallback.has(key)) {
				return memoryFallback.get(key) ?? null;
			}

			try {
				return getStorage().getItem(key);
			} catch {
				return null;
			}
		},
		write(key, value) {
			try {
				const storage = getStorage();
				if (value === undefined) {
					storage.removeItem(key);
				} else {
					storage.setItem(key, value);
				}
				// Storage took it and is the truth again, so drop any override.
				memoryFallback.delete(key);
			} catch {
				memoryFallback.set(key, value ?? null);
			}
		},
	};
}

const localStorageAccess = createWebStorageAccess(() => window.localStorage);
const sessionStorageAccess = createWebStorageAccess(
	() => window.sessionStorage,
);

// One-off accessors for the preference and dismissed-flag cases, which read
// better than reaching for the accessor objects.
function readLocalStorage(key: string): string | null {
	return localStorageAccess.read(key);
}

function writeLocalStorage(key: string, value: string): void {
	localStorageAccess.write(key, value);
}

function readSessionStorage(key: string): string | null {
	return sessionStorageAccess.read(key);
}

function writeSessionStorage(key: string, value: string): void {
	sessionStorageAccess.write(key, value);
}

export type { WebStorageAccess };
export {
	createWebStorageAccess,
	localStorageAccess,
	sessionStorageAccess,
	readLocalStorage,
	writeLocalStorage,
	readSessionStorage,
	writeSessionStorage,
};
